import {
  useState,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
  ReactNode,
  useReducer,
} from "react";

type ContextData = {
  studentName: string;
  setStudentName: Dispatch<SetStateAction<string>>;
  studentId: number;
  setStudentId: Dispatch<SetStateAction<number>>;
  orgId: number;
  setOrgId: Dispatch<SetStateAction<number>>;
  gaugeValue: number;
  setGaugeValue: Dispatch<SetStateAction<number>>;
  showLogin: boolean;
  setShowLogin: Dispatch<SetStateAction<any>>;
  completedLessons: number[];
  setCompletedLessons: Dispatch<SetStateAction<number[]>>;
  activeQuestionPaper: {
    [key: number]: { name: string; active: boolean; classId: number };
  };
  setActiveQuestionPaper: Dispatch<
    SetStateAction<{
      [key: number]: { name: string; active: boolean; classId: number };
    }>
  >;
  pendingLessons: string[];
  setPendingLessons: React.Dispatch<React.SetStateAction<string[]>>;
  upcomingLessons: string[];
  setUpcomingLessons: React.Dispatch<React.SetStateAction<string[]>>;
  questionScores: {
    questionBankId: number;
    questionPaperId: number;
    score: number;
    layoutNumber: number;
    sectionName: string;
  }[];
  setQuestionScores: Dispatch<
    SetStateAction<
      {
        questionBankId: number;
        questionPaperId: number;
        score: number;
        layoutNumber: number;
        sectionName: string;
      }[]
    >
  >;

  examAnswers: {
    questionBankId: number;
    acutalOptionValue: string;
    enteredOptionValue:
      | string
      | ((direction: any, number: any, answer: any) => void);
  }[];

  setExamAnswers: Dispatch<
    SetStateAction<
      {
        questionBankId: number;
        acutalOptionValue: string;
        enteredOptionValue:
          | string
          | ((direction: any, number: any, answer: any) => void);
      }[]
    >
  >;
  isViewMode: boolean;
  setIsViewMode: Dispatch<SetStateAction<boolean>>;
  studentLevel: string;
  setStudentLevel: Dispatch<SetStateAction<string>>;
};

export const Context = createContext<ContextData>({} as ContextData);

export const CharactersContext = ({ children }: { children: ReactNode }) => {
  const [showLogin, setShowLogin] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [questionScores, setQuestionScores] = useState<
    {
      questionBankId: number;
      questionPaperId: number;
      score: number;
      layoutNumber: number;
      sectionName: string;
    }[]
  >([]);
  const [examAnswers, setExamAnswers] = useState<
    {
      questionBankId: number;
      acutalOptionValue: string;
      enteredOptionValue:
        | string
        | ((direction: any, number: any, answer: any) => void);
    }[]
  >([]);
  const [completedLessons, setCompletedLessons] = useState<number[]>([]);
  const [studentName, setStudentName] = useState<string>(() => localStorage.getItem("studentName") || "");
  const [studentId, setStudentId] = useState<number>(() => Number(localStorage.getItem("studentId")) || 0);
  const [orgId, setOrgId] = useState<number>(() => Number(localStorage.getItem("orgId")) || 0);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const [gaugeValue, setGaugeValue] = useState(0);
  const [activeQuestionPaper, setActiveQuestionPaper] = useState<{
    [key: number]: { name: string; active: boolean; classId: number };
  }>(() => ({}));

  const [pendingLessons, setPendingLessons] = useState<string[]>([]);
  const [upcomingLessons, setUpcomingLessons] = useState<string[]>([]);
  const [studentLevel, setStudentLevel] = useState("");

  return (
    <Context.Provider
      value={{
        showLogin,
        setShowLogin,
        studentName,
        setStudentName,
        studentId,
        setStudentId,
        orgId,
        setOrgId,
        questionScores,
        setQuestionScores,
        isViewMode,
        setIsViewMode,
        examAnswers,
        setExamAnswers,
        activeQuestionPaper,
        setActiveQuestionPaper,
        completedLessons,
        setCompletedLessons,
        pendingLessons,
        setPendingLessons,
        upcomingLessons,
        setUpcomingLessons,
        gaugeValue,
        setGaugeValue,
        studentLevel,
        setStudentLevel
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useCharactersContext = () => useContext(Context);
